/** One animal's detail card inside the quantity-based registration form. */
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QrCode } from "lucide-react";

const ID_MARKS = ["White Spot", "Black Spot", "Broken Horn", "Tail Cut", "Ear Cut", "No Mark", "Other"];

export function AnimalSubform({ a, idx, showTitle, typeOptions, breeds, colours,
                                onTypeChange, patchAnimal, onScanRequest }) {
  return (
    <div className="rounded-2xl border p-4" data-testid={`animal-subform-${idx}`}>
      {showTitle && (
        <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-3">Animal {idx + 1}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Animal Type *</label>
          <Select value={a.animal_type} onValueChange={(v) => onTypeChange(idx, v)}>
            <SelectTrigger className="am-input mt-1" data-testid={`new-reg-animal-type-${idx}`}><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {typeOptions.map((t2) => (
                <SelectItem key={t2} value={t2} className="capitalize">{t2}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Gender</label>
          <Select value={a.gender} onValueChange={(v) => patchAnimal(idx, "gender", v)}>
            <SelectTrigger className="am-input mt-1" data-testid={`new-reg-gender-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="male">Male</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Breed</label>
          <Select value={a.breed} onValueChange={(v) => patchAnimal(idx, "breed", v)} disabled={!a.animal_type}>
            <SelectTrigger className="am-input mt-1" data-testid={`new-reg-breed-${idx}`}>
              <SelectValue placeholder={a.animal_type ? "Select breed" : "Pick type first"} />
            </SelectTrigger>
            <SelectContent>
              {breeds.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {a.breed === "Other" && (
            <Input className="am-input mt-2" placeholder="Enter breed" value={a.breed_custom}
              onChange={(e) => patchAnimal(idx, "breed_custom", e.target.value)} data-testid={`new-reg-breed-other-${idx}`} />
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Colour</label>
          <Select value={a.colour} onValueChange={(v) => patchAnimal(idx, "colour", v)} disabled={!a.animal_type}>
            <SelectTrigger className="am-input mt-1" data-testid={`new-reg-colour-${idx}`}>
              <SelectValue placeholder={a.animal_type ? "Select colour" : "Pick type first"} />
            </SelectTrigger>
            <SelectContent>
              {colours.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {a.colour === "Other" && (
            <Input className="am-input mt-2" placeholder="Enter colour" value={a.colour_custom}
              onChange={(e) => patchAnimal(idx, "colour_custom", e.target.value)} data-testid={`new-reg-colour-other-${idx}`} />
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Age</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <Input data-testid={`new-reg-age-years-${idx}`} type="number" min={0} placeholder="Years" className="am-input"
              value={a.age_years} onChange={(e) => patchAnimal(idx, "age_years", e.target.value.replace(/[^\d]/g, ""))} />
            <Input data-testid={`new-reg-age-months-${idx}`} type="number" min={0} max={11} placeholder="Months" className="am-input"
              value={a.age_months} onChange={(e) => patchAnimal(idx, "age_months", e.target.value.replace(/[^\d]/g, "").slice(0, 2))} />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Weight (kg)</label>
          <Input data-testid={`new-reg-weight-${idx}`} inputMode="decimal" placeholder="e.g. 250" className="am-input mt-1"
            value={a.weight_kg} onChange={(e) => patchAnimal(idx, "weight_kg", e.target.value.replace(/[^\d.]/g, ""))} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Sale Price (₹)</label>
          <Input data-testid={`new-reg-sale-price-${idx}`} inputMode="numeric" placeholder="0" className="am-input mt-1"
            value={a.sale_price ? Number(String(a.sale_price).replace(/\D/g, "") || 0).toLocaleString("en-IN") : ""}
            onChange={(e) => patchAnimal(idx, "sale_price", e.target.value.replace(/\D/g, ""))} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Ear Tag</label>
          <div className="flex gap-2 mt-1">
            <Input data-testid={`new-reg-ear-tag-${idx}`} className="am-input flex-1" placeholder="Manual entry or scan"
              value={a.ear_tag} onChange={(e) => patchAnimal(idx, "ear_tag", e.target.value)} />
            <Button type="button" variant="outline" className="rounded-full shrink-0" onClick={() => onScanRequest(idx)}
              data-testid={`new-reg-ear-tag-scan-${idx}`}>
              <QrCode className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Identification Mark</label>
          <Select value={a.identification_mark} onValueChange={(v) => patchAnimal(idx, "identification_mark", v)}>
            <SelectTrigger className="am-input mt-1" data-testid={`new-reg-id-mark-${idx}`}><SelectValue placeholder="Select mark" /></SelectTrigger>
            <SelectContent>{ID_MARKS.map((m2) => <SelectItem key={m2} value={m2}>{m2}</SelectItem>)}</SelectContent>
          </Select>
          {a.identification_mark === "Other" && (
            <Input className="am-input mt-2" placeholder="Describe the mark" value={a.identification_custom}
              onChange={(e) => patchAnimal(idx, "identification_custom", e.target.value)} data-testid={`new-reg-id-mark-other-${idx}`} />
          )}
        </div>
        {/* Photos UI removed for faster field registration — the photo
            architecture (schema, upload endpoint, offline photo queue) is
            kept intact for a future optional feature. */}
      </div>
    </div>
  );
}
